import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

const SUBJECTS = ['question', 'data', 'report', 'billing', 'other'] as const;
type Subject = typeof SUBJECTS[number];

export function ContactPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email ?? '');
  const [subject, setSubject] = useState<Subject>('question');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/contact', {
        method: 'POST',
        body: JSON.stringify({ email, subject, message, website }),
      });
      setSent(true);
    } catch (err: unknown) {
      const code = (err as { data?: { error?: string } })?.data?.error;
      if (code === 'rate_limit') setError(t('contact.errRateLimit'));
      else if (code === 'invalid_email') setError(t('contact.errEmail'));
      else if (code === 'invalid_message') setError(t('contact.errMessage'));
      else setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <main className="page contact">
        <div className="contact-card">
          <h1>{t('contact.title')}</h1>
          <p className="contact-success" role="status">{t('contact.sent')}</p>
          <Link to="/" className="btn secondary block">{t('gallery.home')}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page contact">
      <div className="contact-card">
        <h1>{t('contact.title')}</h1>
        <p className="sub">{t('contact.lead')}</p>
        <form className="contact-form" onSubmit={handleSubmit}>
          <label htmlFor="contact-email">{t('contact.email')}</label>
          <input
            id="contact-email"
            type="email"
            required
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />

          <label htmlFor="contact-subject">{t('contact.subject')}</label>
          <select
            id="contact-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value as Subject)}
          >
            {SUBJECTS.map((s) => (
              <option key={s} value={s}>{t(`contact.subject.${s}`)}</option>
            ))}
          </select>

          <label htmlFor="contact-message">{t('contact.message')}</label>
          <textarea
            id="contact-message"
            required
            minLength={10}
            maxLength={3000}
            rows={8}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          {/* Honeypot anti-bot : caché aux humains */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="contact-honeypot"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {error && <p className="contact-error" role="alert">{error}</p>}

          <button type="submit" className="btn primary block" disabled={busy}>
            {busy ? t('common.loading') : t('contact.send')}
          </button>
        </form>
        <p className="contact-privacy muted">
          {t('contact.privacyHint')}{' '}
          <Link to="/confidentialite">{t('legal.privacy')}</Link>
        </p>
      </div>
    </main>
  );
}
